import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import JSZip from "jszip";
import Navbar from "../navbar/Navbar";
import { createModel } from "../../services/aiModels";
import "./Models.css";
import "../shared/starfield.css";

const TOOL_TYPES = [
    { value: 'tool_signal', label: 'Signal Processing' },
    { value: 'tool_table', label: 'Data Processing' },
    { value: 'tool_custom', label: 'Custom Tool' },
];

const INPUT_TYPES = [
    { value: 'signal', label: 'Signal (EDF/BDF/CSV)' },
    { value: 'csv', label: 'CSV / Table' },
    { value: 'dicom', label: 'DICOM' },
    { value: 'nifti', label: 'NIfTI' },
    { value: 'imaging', label: 'Any Medical Imaging' },
    { value: 'any', label: 'Any' },
];

const ICONS = [
    { value: 'fa-wrench', label: 'Wrench' },
    { value: 'fa-cogs', label: 'Cogs' },
    { value: 'fa-heartbeat', label: 'Heartbeat' },
    { value: 'fa-chart-line', label: 'Chart' },
    { value: 'fa-filter', label: 'Filter' },
    { value: 'fa-calculator', label: 'Calculator' },
    { value: 'fa-bolt', label: 'Bolt' },
    { value: 'fa-flask', label: 'Flask' },
    { value: 'fa-magic', label: 'Magic' },
    { value: 'fa-stethoscope', label: 'Stethoscope' },
];

const COLORS = [
    '#6c757d', // Gray
    '#007bff', // Blue
    '#28a745', // Green
    '#dc3545', // Red
    '#fd7e14', // Orange
    '#17a2b8', // Cyan
    '#e83e8c', // Pink
    '#6f42c1', // Purple
];

const NewTool = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [showGuide, setShowGuide] = useState(false);
    const [isParsingZip, setIsParsingZip] = useState(false);
    const [parametersAutoLoaded, setParametersAutoLoaded] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        model_type: 'tool_signal',
        input_types: 'any',
        docker_command: 'python /DV/run.py',
        gpu_required: false,
        gpu_device_ids: '',
        output_format: 'csv',
        output_directory: 'tools/{model_name}/',
        estimated_runtime: 60,
        version: '1.0',
        icon: 'fa-wrench',
        color: '#6c757d',
        parameters_schema: {},
        default_parameters: {},
    });

    // Parameter builder state
    const [newParam, setNewParam] = useState({
        key: '',
        type: 'text',
        label: '',
        description: '',
        options: '',
        default: '',
    });

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Extract and parse parameters.json from ZIP file
    const parseZipForParameters = async (file) => {
        setIsParsingZip(true);
        try {
            const zip = await JSZip.loadAsync(file);

            // Find parameters.json (might be in root or subdirectory)
            let parametersFile = null;
            zip.forEach((relativePath, zipEntry) => {
                if (relativePath.endsWith('parameters.json') && !zipEntry.dir) {
                    parametersFile = zipEntry;
                }
            });

            if (parametersFile) {
                const content = await parametersFile.async('string');
                const paramsConfig = JSON.parse(content);

                // Auto-populate form data from parameters.json
                const updates = {};

                // Tool info (check both tool_info and model_info)
                const toolInfo = paramsConfig.tool_info || paramsConfig.model_info || {};
                if (toolInfo.name && !formData.name) {
                    updates.name = toolInfo.name;
                }
                if (toolInfo.description) {
                    updates.description = toolInfo.description;
                }
                if (toolInfo.version) {
                    updates.version = toolInfo.version;
                }
                if (toolInfo.type || toolInfo.model_type) {
                    updates.model_type = toolInfo.type || toolInfo.model_type;
                }
                if (toolInfo.input_types) {
                    updates.input_types = toolInfo.input_types;
                }
                if (toolInfo.output_format) {
                    updates.output_format = toolInfo.output_format;
                }
                if (toolInfo.gpu_required !== undefined) {
                    updates.gpu_required = toolInfo.gpu_required;
                }
                if (toolInfo.estimated_runtime) {
                    updates.estimated_runtime = toolInfo.estimated_runtime;
                }
                if (toolInfo.icon) {
                    updates.icon = toolInfo.icon;
                }
                if (toolInfo.color) {
                    updates.color = toolInfo.color;
                }

                // Parameters schema
                if (paramsConfig.parameters_schema) {
                    updates.parameters_schema = paramsConfig.parameters_schema;
                }

                // Default parameters
                if (paramsConfig.default_parameters) {
                    updates.default_parameters = paramsConfig.default_parameters;
                }

                // Update form data
                if (Object.keys(updates).length > 0) {
                    setFormData(prev => ({ ...prev, ...updates }));
                    setParametersAutoLoaded(true);

                    const fieldsLoaded = [];
                    if (updates.name) fieldsLoaded.push('name');
                    if (updates.description) fieldsLoaded.push('description');
                    if (updates.model_type) fieldsLoaded.push('tool type');
                    if (updates.parameters_schema && Object.keys(updates.parameters_schema).length > 0) {
                        fieldsLoaded.push(`${Object.keys(updates.parameters_schema).length} parameters`);
                    }

                    toast.success(
                        `Auto-loaded from parameters.json: ${fieldsLoaded.join(', ')}`,
                        { autoClose: 5000 }
                    );
                }
            }
        } catch (err) {
            console.error('Error parsing ZIP for parameters:', err);
        } finally {
            setIsParsingZip(false);
        }
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.name.endsWith('.zip')) {
                toast.error('Please select a ZIP file containing your Dockerfile and run.py');
                return;
            }
            if (file.size > 500 * 1024 * 1024) {
                toast.error('File size must be less than 500MB');
                return;
            }
            setSelectedFile(file);
            setParametersAutoLoaded(false);
            await parseZipForParameters(file);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files[0];
        if (file) {
            if (!file.name.endsWith('.zip')) {
                toast.error('Please drop a ZIP file containing your Dockerfile and run.py');
                return;
            }
            setSelectedFile(file);
            setParametersAutoLoaded(false);
            await parseZipForParameters(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const addParameter = () => {
        if (!newParam.key || !newParam.label) {
            toast.warning('Parameter key and label are required');
            return;
        }

        const paramConfig = {
            type: newParam.type,
            label: newParam.label,
        };

        if (newParam.description) {
            paramConfig.description = newParam.description;
        }

        if (newParam.type === 'select' || newParam.type === 'multiselect') {
            paramConfig.options = newParam.options.split(',').map(o => o.trim()).filter(o => o);
        }

        if (newParam.default) {
            if (newParam.type === 'number') {
                paramConfig.default = parseFloat(newParam.default);
            } else if (newParam.type === 'boolean') {
                paramConfig.default = newParam.default === 'true';
            } else if (newParam.type === 'multiselect') {
                paramConfig.default = newParam.default.split(',').map(o => o.trim());
            } else {
                paramConfig.default = newParam.default;
            }
        }

        setFormData(prev => ({
            ...prev,
            parameters_schema: {
                ...prev.parameters_schema,
                [newParam.key]: paramConfig
            }
        }));

        setNewParam({
            key: '',
            type: 'text',
            label: '',
            description: '',
            options: '',
            default: '',
        });

        toast.success(`Parameter "${newParam.label}" added`);
    };

    const removeParameter = (key) => {
        setFormData(prev => {
            const newSchema = { ...prev.parameters_schema };
            delete newSchema[key];
            return { ...prev, parameters_schema: newSchema };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Tool name is required');
            return;
        }

        if (!selectedFile) {
            toast.error('Please upload a tool package (ZIP file with Dockerfile and run.py)');
            return;
        }

        setIsSubmitting(true);

        try {
            const submitData = new FormData();
            submitData.append('name', formData.name);
            submitData.append('description', formData.description || '');
            submitData.append('model_type', formData.model_type);
            submitData.append('input_types', formData.input_types);
            submitData.append('docker_command', formData.docker_command);
            submitData.append('gpu_required', formData.gpu_required);
            submitData.append('gpu_device_ids', formData.gpu_device_ids);
            submitData.append('output_format', formData.output_format);
            submitData.append('output_directory', formData.output_directory);
            submitData.append('estimated_runtime', formData.estimated_runtime);
            submitData.append('version', formData.version);
            submitData.append('icon', formData.icon);
            submitData.append('color', formData.color);
            submitData.append('parameters_schema', JSON.stringify(formData.parameters_schema));
            submitData.append('default_parameters', JSON.stringify(formData.default_parameters));
            submitData.append('model_package', selectedFile);
            submitData.append('docker_image', 'placeholder');
            // Mark as tool entity type
            submitData.append('entity_type', 'tool');

            const tool = await createModel(submitData);
            toast.success(`Tool "${tool.name}" created! Docker build started automatically.`);
            toast.info('Check the Models & Tools page for build status.', { autoClose: 8000 });

            navigate('/Models');
        } catch (err) {
            console.error('Error creating tool:', err);
            const errorMsg = err.response?.data?.error ||
                             err.response?.data?.detail ||
                             Object.values(err.response?.data || {}).flat().join(', ') ||
                             'Failed to create tool';
            toast.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="models-page">
            <div className="starfield-background"></div>

            <Navbar />

            <div className="container py-4">
                {/* Header */}
                <div className="models-header-card">
                    <div className="models-header-content">
                        <div className="models-info">
                            <h1 className="models-title">
                                <i className="fa fa-plus-circle me-2"></i>
                                Create New Tool
                            </h1>
                            <p className="models-description">
                                Upload your processing tool package and configure its parameters
                            </p>
                        </div>
                        <button
                            className="btn-back-models"
                            onClick={() => navigate('/Models')}
                        >
                            <i className="fa fa-arrow-left me-2"></i>
                            Back to Models & Tools
                        </button>
                    </div>
                </div>

                {/* Developer Guide */}
                <div className="new-model-card developer-guide-card">
                    <div
                        className="new-model-card-header clickable"
                        onClick={() => setShowGuide(!showGuide)}
                        style={{ cursor: 'pointer' }}
                    >
                        <i className="fa fa-book"></i>
                        <h3>Developer Guide</h3>
                        <i className={`fa fa-chevron-${showGuide ? 'up' : 'down'} ms-auto`}></i>
                    </div>
                    {showGuide && (
                        <div className="new-model-card-content developer-guide-content">
                            <div className="guide-section">
                                <h4><i className="fa fa-folder-open me-2"></i>Required Files</h4>
                                <p>Your ZIP package must contain:</p>
                                <pre className="code-block">{`YourTool/
├── Dockerfile      # Docker build instructions
├── run.py          # Your processing code
└── requirements.txt  # Python dependencies`}</pre>
                            </div>

                            <div className="guide-section">
                                <h4><i className="fa fa-code me-2"></i>Simple Interface</h4>
                                <p>Your <code>run.py</code> reads input and writes output:</p>
                                <pre className="code-block">{`import os, json

# Read parameters
params = json.loads(os.environ.get('DV_PARAMS', '{}'))

# Input directory: /DV/data/
input_dir = '/DV/data/'

# Output directory: /DV/output/
output_dir = '/DV/output/'

# Your processing logic here
# ... read input files from input_dir
# ... process data
# ... write results to output_dir`}</pre>
                            </div>

                            <div className="guide-section">
                                <h4><i className="fa fa-file-export me-2"></i>Output Formats</h4>
                                <table className="guide-table">
                                    <thead>
                                        <tr>
                                            <th>Tool Type</th>
                                            <th>Output</th>
                                            <th>Example</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Signal Processing</td>
                                            <td>CSV signal</td>
                                            <td><code>/DV/output/result.csv</code></td>
                                        </tr>
                                        <tr>
                                            <td>Data Processing</td>
                                            <td>CSV table</td>
                                            <td><code>/DV/output/table.csv</code></td>
                                        </tr>
                                        <tr>
                                            <td>Custom</td>
                                            <td>JSON / CSV / Any</td>
                                            <td><code>/DV/output/result.json</code></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="guide-section">
                                <h4><i className="fa fa-sliders-h me-2"></i>Parameters</h4>
                                <p>Parameters you define below will be available via <code>DV_PARAMS</code> env var:</p>
                                <pre className="code-block">{`import os, json

params = json.loads(os.environ.get('DV_PARAMS', '{}'))
channel = params.get('channel_index', 0)
sample_rate = params.get('sampling_frequency', 256)`}</pre>
                            </div>
                        </div>
                    )}
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Tool Package Upload */}
                    <div className="new-model-card">
                        <div className="new-model-card-header">
                            <i className="fa fa-upload"></i>
                            <h3>Tool Package *</h3>
                        </div>
                        <div className="new-model-card-content">
                            <div
                                className={`file-upload-zone ${selectedFile ? 'has-file' : ''}`}
                                onClick={() => fileInputRef.current?.click()}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept=".zip"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                                {selectedFile ? (
                                    <div className="file-selected">
                                        <i className="fa fa-file-archive"></i>
                                        <div className="file-info">
                                            <span className="file-name">{selectedFile.name}</span>
                                            <span className="file-size">
                                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                                {isParsingZip && (
                                                    <span className="ms-2">
                                                        <i className="fa fa-spinner fa-spin"></i> Reading...
                                                    </span>
                                                )}
                                                {parametersAutoLoaded && !isParsingZip && (
                                                    <span className="ms-2 text-success">
                                                        <i className="fa fa-check-circle"></i> Config loaded
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn-remove-file"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedFile(null);
                                                setParametersAutoLoaded(false);
                                            }}
                                        >
                                            <i className="fa fa-times"></i>
                                        </button>
                                    </div>
                                ) : (
                                    <div className="upload-placeholder">
                                        <i className="fa fa-cloud-upload-alt"></i>
                                        <p>Drag and drop your ZIP file here</p>
                                        <span>or click to browse</span>
                                    </div>
                                )}
                            </div>
                            <div className="upload-requirements">
                                <h4>Package Requirements:</h4>
                                <ul>
                                    <li><i className="fa fa-check"></i> <strong>Dockerfile</strong> - Instructions to build your tool's Docker image</li>
                                    <li><i className="fa fa-check"></i> <strong>run.py</strong> - Entry point script that processes input data</li>
                                    <li><i className="fa fa-magic text-primary"></i> <strong>parameters.json</strong> (optional) - Auto-configure all fields below</li>
                                    <li><i className="fa fa-info-circle"></i> Any additional scripts, libraries, or dependencies</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Basic Information */}
                    <div className="new-model-card">
                        <div className="new-model-card-header">
                            <i className="fa fa-info-circle"></i>
                            <h3>Basic Information</h3>
                        </div>
                        <div className="new-model-card-content">
                            <div className="form-row">
                                <div className="form-group flex-grow">
                                    <label htmlFor="name">Tool Name *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="e.g., HR Signal Extractor, Data Filter"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="version">Version</label>
                                    <input
                                        type="text"
                                        id="version"
                                        name="version"
                                        value={formData.version}
                                        onChange={handleInputChange}
                                        placeholder="1.0"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="Describe what your tool does..."
                                    rows={3}
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="model_type">Tool Type</label>
                                    <select
                                        id="model_type"
                                        name="model_type"
                                        value={formData.model_type}
                                        onChange={handleInputChange}
                                    >
                                        {TOOL_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="input_types">Input Type</label>
                                    <select
                                        id="input_types"
                                        name="input_types"
                                        value={formData.input_types}
                                        onChange={handleInputChange}
                                    >
                                        {INPUT_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="output_format">Output Format</label>
                                    <select
                                        id="output_format"
                                        name="output_format"
                                        value={formData.output_format}
                                        onChange={handleInputChange}
                                    >
                                        <option value="csv">CSV</option>
                                        <option value="json">JSON</option>
                                        <option value="nifti">NIfTI (.nii.gz)</option>
                                        <option value="dicom">DICOM</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="gpu_required"
                                            checked={formData.gpu_required}
                                            onChange={handleInputChange}
                                            className="me-2"
                                        />
                                        Requires GPU
                                    </label>
                                    <small className="text-muted d-block mt-1">
                                        Most processing tools don't need a GPU
                                    </small>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Icon</label>
                                    <div className="icon-selector">
                                        {ICONS.map(icon => (
                                            <button
                                                key={icon.value}
                                                type="button"
                                                className={`icon-btn ${formData.icon === icon.value ? 'active' : ''}`}
                                                onClick={() => setFormData(prev => ({ ...prev, icon: icon.value }))}
                                                title={icon.label}
                                                style={{ color: formData.color }}
                                            >
                                                <i className={`fa ${icon.value}`}></i>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Color</label>
                                    <div className="color-selector">
                                        {COLORS.map(color => (
                                            <button
                                                key={color}
                                                type="button"
                                                className={`color-btn ${formData.color === color ? 'active' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => setFormData(prev => ({ ...prev, color }))}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tool Parameters */}
                    <div className="new-model-card">
                        <div className="new-model-card-header">
                            <i className="fa fa-sliders-h"></i>
                            <h3>Tool Parameters</h3>
                            <small>Define parameters users can configure when running the tool</small>
                        </div>
                        <div className="new-model-card-content">
                            {/* Existing parameters */}
                            {Object.keys(formData.parameters_schema).length > 0 && (
                                <div className="parameters-list">
                                    {Object.entries(formData.parameters_schema).map(([key, config]) => (
                                        <div key={key} className="parameter-item">
                                            <div className="parameter-info">
                                                <span className="parameter-key">{key}</span>
                                                <span className="parameter-label">{config.label}</span>
                                                <span className="parameter-type badge">{config.type}</span>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn-remove-param"
                                                onClick={() => removeParameter(key)}
                                            >
                                                <i className="fa fa-trash"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Add new parameter */}
                            <div className="add-parameter-form">
                                <h4>Add Parameter</h4>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Key</label>
                                        <input
                                            type="text"
                                            value={newParam.key}
                                            onChange={(e) => setNewParam(prev => ({ ...prev, key: e.target.value }))}
                                            placeholder="e.g., channel_index"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Label</label>
                                        <input
                                            type="text"
                                            value={newParam.label}
                                            onChange={(e) => setNewParam(prev => ({ ...prev, label: e.target.value }))}
                                            placeholder="e.g., Channel Index"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Type</label>
                                        <select
                                            value={newParam.type}
                                            onChange={(e) => setNewParam(prev => ({ ...prev, type: e.target.value }))}
                                        >
                                            <option value="text">Text</option>
                                            <option value="number">Number</option>
                                            <option value="boolean">Boolean (Checkbox)</option>
                                            <option value="select">Single Select</option>
                                            <option value="multiselect">Multi Select</option>
                                        </select>
                                    </div>
                                </div>
                                {(newParam.type === 'select' || newParam.type === 'multiselect') && (
                                    <div className="form-group">
                                        <label>Options (comma-separated)</label>
                                        <input
                                            type="text"
                                            value={newParam.options}
                                            onChange={(e) => setNewParam(prev => ({ ...prev, options: e.target.value }))}
                                            placeholder="e.g., ecg, ppg, eeg"
                                        />
                                    </div>
                                )}
                                <div className="form-row">
                                    <div className="form-group flex-grow">
                                        <label>Description (optional)</label>
                                        <input
                                            type="text"
                                            value={newParam.description}
                                            onChange={(e) => setNewParam(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Help text for this parameter"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Default Value</label>
                                        <input
                                            type="text"
                                            value={newParam.default}
                                            onChange={(e) => setNewParam(prev => ({ ...prev, default: e.target.value }))}
                                            placeholder="Default value"
                                        />
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn-add-param"
                                    onClick={addParameter}
                                >
                                    <i className="fa fa-plus me-2"></i>
                                    Add Parameter
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={() => navigate('/Models')}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Creating Tool...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-save me-2"></i>
                                    Create Tool
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewTool;
